import React, { useEffect, useState } from "react";
import Layout, { PageHeader } from "@/components/Layout";
import { api, formatApiError } from "@/lib/api";
import {
  Plus, PencilSimple, Trash, Bell, PaperPlaneTilt, EnvelopeSimple,
  DiscordLogo, SlackLogo, TelegramLogo, MicrosoftTeamsLogo, DeviceMobile, Globe, Lightning,
} from "@phosphor-icons/react";

const TYPES = [
  { v: "email",          l: "Email",                  Icon: EnvelopeSimple,     desc: "Send to recipient email(s) via your SMTP (Integrations)." },
  { v: "sms",            l: "SMS (Twilio)",           Icon: DeviceMobile,       desc: "Send SMS via Twilio." },
  { v: "slack",          l: "Slack webhook",          Icon: SlackLogo,          desc: "Post to a Slack channel via incoming webhook. Add signing secret for two-way Ack." },
  { v: "discord",        l: "Discord webhook",        Icon: DiscordLogo,        desc: "Post to a Discord channel via webhook." },
  { v: "teams",          l: "Microsoft Teams (Webhook)", Icon: MicrosoftTeamsLogo, desc: "MessageCard via Teams incoming webhook (deprecating in 2026)." },
  { v: "teams_graph",    l: "Microsoft Teams (Graph API)", Icon: MicrosoftTeamsLogo, desc: "Direct post via Microsoft Graph. Requires Azure AD app with ChannelMessage.Send + Admin Consent." },
  { v: "power_automate", l: "Power Automate (Teams)", Icon: Lightning,          desc: "Power Automate HTTP-trigger flow → posts in Teams. The recommended Teams path for 2026+." },
  { v: "telegram",       l: "Telegram",               Icon: TelegramLogo,       desc: "Telegram bot — needs bot token + chat ID." },
  { v: "webhook",        l: "Generic webhook",        Icon: Globe,              desc: "POST JSON to any URL." },
];

const EMPTY = {
  name: "", type: "email", enabled: true,
  email_to: "", sms_to: "",
  twilio_account_sid: "", twilio_auth_token: "", twilio_from_number: "",
  webhook_url: "", telegram_bot_token: "", telegram_chat_id: "",
  graph_tenant_id: "", graph_client_id: "", graph_client_secret: "",
  graph_team_id: "", graph_team_name: "", graph_channel_id: "", graph_channel_name: "General",
  slack_signing_secret: "",
};

export default function AlertChannels() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState("");
  const [testing, setTesting] = useState({});
  const [testResults, setTestResults] = useState({});

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/alert-channels"); setItems(data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm({ ...EMPTY }); setEditing("new"); setError(""); };
  const openEdit = (ch) => {
    // Coerce all nulls to empty string so controlled inputs stay controlled
    // and previously-typed values don't leak across edits (e.g. masked secrets).
    const cleaned = Object.fromEntries(
      Object.entries(ch).map(([k, v]) => [k, v == null ? "" : v])
    );
    setForm({
      ...EMPTY, ...cleaned,
      email_to: (ch.email_to || []).join(", "),
      sms_to: (ch.sms_to || []).join(", "),
    });
    setEditing(ch); setError("");
  };

  const closeModal = () => {
    setEditing(null);
    setForm({ ...EMPTY });
    setError("");
  };

  const save = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const payload = {
        name: form.name, type: form.type, enabled: !!form.enabled,
        email_to: String(form.email_to || "").split(",").map(s => s.trim()).filter(Boolean),
        sms_to: String(form.sms_to || "").split(",").map(s => s.trim()).filter(Boolean),
        twilio_account_sid: form.twilio_account_sid || null,
        twilio_auth_token: (form.twilio_auth_token === "********" || !form.twilio_auth_token) ? form.twilio_auth_token || null : form.twilio_auth_token,
        twilio_from_number: form.twilio_from_number || null,
        webhook_url: (form.webhook_url === "********" || !form.webhook_url) ? form.webhook_url || null : form.webhook_url,
        telegram_bot_token: (form.telegram_bot_token === "********" || !form.telegram_bot_token) ? form.telegram_bot_token || null : form.telegram_bot_token,
        telegram_chat_id: form.telegram_chat_id || null,
        graph_tenant_id: form.graph_tenant_id || null,
        graph_client_id: form.graph_client_id || null,
        graph_client_secret: (form.graph_client_secret === "********" || !form.graph_client_secret) ? form.graph_client_secret || null : form.graph_client_secret,
        graph_team_id: form.graph_team_id || null,
        graph_team_name: form.graph_team_name || null,
        graph_channel_id: form.graph_channel_id || null,
        graph_channel_name: form.graph_channel_name || "General",
        slack_signing_secret: (form.slack_signing_secret === "********" || !form.slack_signing_secret) ? form.slack_signing_secret || null : form.slack_signing_secret,
      };
      if (editing === "new") await api.post("/alert-channels", payload);
      else await api.put(`/alert-channels/${editing.id}`, payload);
      closeModal();
      await load();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete channel?")) return;
    await api.delete(`/alert-channels/${id}`); await load();
  };

  const sendTest = async (id) => {
    setTesting(t => ({ ...t, [id]: true }));
    try {
      const { data } = await api.post(`/alert-channels/${id}/test`);
      setTestResults(r => ({ ...r, [id]: data }));
    } catch (e) {
      setTestResults(r => ({ ...r, [id]: { ok: false, message: formatApiError(e.response?.data?.detail) || e.message } }));
    } finally {
      setTesting(t => ({ ...t, [id]: false }));
    }
  };

  const typeMeta = (v) => TYPES.find(t => t.v === v) || TYPES[0];

  return (
    <Layout>
      <PageHeader
        title="Alert channels"
        subtitle="Where alerts go. Email, SMS, Slack, Teams (Webhook/Graph/Power Automate), Discord, Telegram, generic webhooks."
        actions={
          <button onClick={openNew} data-testid="channels-new-btn"
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-brand-cyan text-brand-navy hover:bg-brand-cyan/90 rounded-md font-semibold transition-colors">
            <Plus size={14} weight="bold" /> New channel
          </button>
        }
      />
      <div className="p-6">
        {loading ? (
          <div className="text-sm text-slate-500">loading…</div>
        ) : items.length === 0 ? (
          <div className="sw-card border-dashed p-16 text-center">
            <Bell size={36} weight="duotone" className="text-brand-cyan mx-auto mb-3" />
            <h3 className="font-display text-lg font-bold tracking-tight mb-1">No alert channels yet</h3>
            <p className="text-sm text-slate-400 mb-4">Add a channel to start getting paged when monitors or backups fail.</p>
            <button onClick={openNew} data-testid="channels-empty-cta"
              className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-brand-cyan text-brand-navy hover:bg-brand-cyan/90 rounded-md font-semibold">
              <Plus size={14} weight="bold" /> Add channel
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="channels-grid">
            {items.map((ch) => {
              const meta = typeMeta(ch.type);
              const last = testResults[ch.id];
              return (
                <div key={ch.id} className="sw-card p-5 sw-card-hover">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 rounded-md bg-brand-cyan/10 border border-brand-cyan/30 flex items-center justify-center shrink-0">
                        <meta.Icon size={20} weight="duotone" className="text-brand-cyan" />
                      </div>
                      <div className="min-w-0">
                        <div className="font-display font-bold text-base truncate">{ch.name}</div>
                        <div className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-400">{meta.l}</div>
                      </div>
                    </div>
                    <span className={`text-[10px] font-semibold uppercase tracking-[0.15em] ${ch.enabled ? "text-emerald-400" : "text-slate-400"}`}>
                      {ch.enabled ? "ON" : "OFF"}
                    </span>
                  </div>
                  <div className="text-xs text-slate-400 mb-3 font-mono break-all min-h-[24px]">
                    {ch.type === "email"    && (ch.email_to || []).join(", ")}
                    {ch.type === "sms"      && (ch.sms_to || []).join(", ")}
                    {(["slack","discord","teams","webhook","power_automate"].includes(ch.type)) && ch.webhook_url}
                    {ch.type === "teams_graph" && `team ${ch.graph_team_name || ch.graph_team_id} → ${ch.graph_channel_name}`}
                    {ch.type === "telegram" && `chat ${ch.telegram_chat_id}`}
                  </div>
                  {ch.type === "slack" && ch.slack_signing_secret && (
                    <div className="text-[10px] text-brand-cyan mb-2">↻ Two-way Ack enabled</div>
                  )}
                  {last && (
                    <div className={`text-[11px] mb-3 px-2 py-1 rounded ${last.ok ?"text-emerald-400 bg-emerald-500/10 border border-emerald-500/30" :"text-red-400 bg-red-500/10 border border-red-500/30"}`}>
                      {last.ok ? "✓ " : "✗ "}{last.message}
                    </div>
                  )}
                  <div className="flex items-center justify-between pt-3 border-t border-border">
                    <button onClick={() => sendTest(ch.id)} disabled={testing[ch.id]} data-testid={`channel-test-${ch.id}`}
                      className="text-xs inline-flex items-center gap-1.5 text-brand-cyan hover:text-white transition-colors">
                      <PaperPlaneTilt size={12} weight="fill" />
                      {testing[ch.id] ? "Sending…" : "Send test"}
                    </button>
                    <div className="flex items-center gap-1">
                      <button onClick={() => openEdit(ch)} data-testid={`channel-edit-${ch.id}`}
                        className="p-1.5 text-slate-300 hover:text-white"><PencilSimple size={14} /></button>
                      <button onClick={() => remove(ch.id)} data-testid={`channel-delete-${ch.id}`}
                        className="p-1.5 text-slate-300 hover:text-red-400"><Trash size={14} /></button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/70 z-30 flex items-center justify-center p-6" onClick={closeModal}>
          <form onSubmit={save} onClick={(e) => e.stopPropagation()}
            className="sw-card w-full max-w-lg p-6 space-y-3 max-h-[90vh] overflow-y-auto" data-testid="channel-form">
            <h3 className="font-display text-lg font-bold tracking-tight">{editing === "new" ? "New alert channel" : "Edit channel"}</h3>

            <FieldA label="Name" required>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="channel-name-input" />
            </FieldA>
            <FieldA label="Type">
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="channel-type-select">
                {TYPES.map(t => <option key={t.v} value={t.v}>{t.l}</option>)}
              </select>
            </FieldA>
            <p className="text-xs text-slate-400 -mt-1">{typeMeta(form.type).desc}</p>

            {form.type === "email" && (
              <FieldA label="Recipient emails (comma-sep)">
                <input value={form.email_to} placeholder="oncall@msp.com, alerts@msp.com" onChange={(e) => setForm({ ...form, email_to: e.target.value })} data-testid="channel-email-to-input" />
              </FieldA>
            )}

            {form.type === "sms" && (
              <>
                <FieldA label="Twilio Account SID">
                  <input value={form.twilio_account_sid} onChange={(e) => setForm({ ...form, twilio_account_sid: e.target.value })} data-testid="channel-twilio-sid-input" />
                </FieldA>
                <FieldA label="Twilio Auth Token">
                  <input type="password" value={form.twilio_auth_token} onChange={(e) => setForm({ ...form, twilio_auth_token: e.target.value })} data-testid="channel-twilio-token-input" />
                </FieldA>
                <FieldA label="From number (E.164)">
                  <input value={form.twilio_from_number} placeholder="+15555550100" onChange={(e) => setForm({ ...form, twilio_from_number: e.target.value })} data-testid="channel-twilio-from-input" />
                </FieldA>
                <FieldA label="Recipients (comma-sep, E.164)">
                  <input value={form.sms_to} placeholder="+15555550111, +15555550222" onChange={(e) => setForm({ ...form, sms_to: e.target.value })} data-testid="channel-sms-to-input" />
                </FieldA>
                <TwilioPreflight sid={form.twilio_account_sid}
                  token={form.twilio_auth_token}
                  fromNumber={form.twilio_from_number} />
              </>
            )}

            {["slack", "discord", "teams", "webhook", "power_automate"].includes(form.type) && (
              <FieldA label="Webhook URL" required>
                <input required value={form.webhook_url}
                  placeholder={
                    form.type === "slack" ? "https://hooks.slack.com/services/..."
                    : form.type === "power_automate" ? "https://prod-XX.westus.logic.azure.com:443/workflows/..."
                    : "https://..."}
                  onChange={(e) => setForm({ ...form, webhook_url: e.target.value })}
                  data-testid="channel-webhook-input" />
              </FieldA>
            )}

            {form.type === "slack" && (
              <FieldA label="Slack Signing Secret (optional — enables two-way Ack buttons)">
                <input type="password" value={form.slack_signing_secret}
                  placeholder="abc123… (from your Slack app's Basic Information page)"
                  onChange={(e) => setForm({ ...form, slack_signing_secret: e.target.value })}
                  data-testid="channel-slack-signing-secret-input" />
              </FieldA>
            )}

            {form.type === "teams_graph" && (
              <>
                <FieldA label="Tenant ID (Azure AD directory)">
                  <input value={form.graph_tenant_id}
                    placeholder="00000000-0000-0000-0000-000000000000"
                    onChange={(e) => setForm({ ...form, graph_tenant_id: e.target.value })}
                    data-testid="channel-graph-tenant-input" />
                </FieldA>
                <FieldA label="Application (Client) ID">
                  <input value={form.graph_client_id}
                    onChange={(e) => setForm({ ...form, graph_client_id: e.target.value })}
                    data-testid="channel-graph-client-id-input" />
                </FieldA>
                <FieldA label="Client Secret">
                  <input type="password" value={form.graph_client_secret}
                    onChange={(e) => setForm({ ...form, graph_client_secret: e.target.value })}
                    data-testid="channel-graph-client-secret-input" />
                </FieldA>
                <FieldA label="Team ID (GUID)">
                  <input value={form.graph_team_id}
                    onChange={(e) => setForm({ ...form, graph_team_id: e.target.value })}
                    data-testid="channel-graph-team-id-input" />
                </FieldA>
                <FieldA label="Team name (optional, for display)">
                  <input value={form.graph_team_name}
                    onChange={(e) => setForm({ ...form, graph_team_name: e.target.value })}
                    data-testid="channel-graph-team-name-input" />
                </FieldA>
                <FieldA label="Channel name">
                  <input value={form.graph_channel_name}
                    onChange={(e) => setForm({ ...form, graph_channel_name: e.target.value })}
                    data-testid="channel-graph-channel-name-input" />
                </FieldA>
                <div className="text-[11px] text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded p-3 space-y-2">
                  <div className="font-bold flex items-center gap-1.5">⚠ HTTP 403 fix — you need RSC, not standard API permissions</div>
                  <div className="text-amber-200/90 font-normal">
                    <strong>ChannelMessage.Send.Group</strong> is a <em>Resource-Specific Consent (RSC)</em> permission — it does NOT appear in the Azure portal &ldquo;API permissions&rdquo; search. Tenant-wide admin consent is not sufficient.
                  </div>
                  <div className="text-amber-200/90 font-normal pt-1 border-t border-amber-500/20">
                    To grant RSC you must (advanced):
                    <ol className="list-decimal list-inside space-y-0.5 mt-1">
                      <li>Declare the permission in your Teams app manifest (Teams Developer Portal).</li>
                      <li>Have the <strong>team owner</strong> install your app into the specific Team and accept RSC.</li>
                    </ol>
                  </div>
                  <a href="https://learn.microsoft.com/en-us/microsoftteams/platform/graph-api/rsc/resource-specific-consent"
                    target="_blank" rel="noopener noreferrer"
                    data-testid="graph-azure-link"
                    className="inline-flex items-center gap-1 text-amber-300 hover:text-amber-100 underline">
                    RSC docs · Microsoft Learn →
                  </a>
                  <div className="text-emerald-300 font-normal pt-2 border-t border-amber-500/20">
                    <strong>✓ Easier path (recommended for 2026+):</strong> Use a <strong>Power Automate</strong> channel (HTTP-trigger flow → &ldquo;Post in a channel as Flow bot&rdquo;). No RSC, no admin consent — works in any tenant in minutes.
                  </div>
                </div>
              </>
            )}

            {form.type === "telegram" && (
              <>
                <FieldA label="Bot token" required>
                  <input required type="password" value={form.telegram_bot_token} onChange={(e) => setForm({ ...form, telegram_bot_token: e.target.value })} data-testid="channel-telegram-token-input" />
                </FieldA>
                <FieldA label="Chat ID" required>
                  <input required value={form.telegram_chat_id} onChange={(e) => setForm({ ...form, telegram_chat_id: e.target.value })} data-testid="channel-telegram-chat-input" />
                </FieldA>
              </>
            )}

            <label className="flex items-center gap-2 text-sm text-slate-200 pt-2">
              <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} data-testid="channel-enabled-checkbox" />
              Enabled
            </label>
            {error && <div className="text-xs text-red-400">{error}</div>}
            <div className="flex justify-end gap-2 pt-2 border-t border-border">
              <button type="button" onClick={closeModal} data-testid="channel-cancel-btn"
                className="px-3 py-1.5 text-sm border border-border hover:bg-white/5 rounded-md">Cancel</button>
              <button type="submit" data-testid="channel-save-btn"
                className="px-3 py-1.5 text-sm bg-brand-cyan text-brand-navy hover:bg-brand-cyan/90 rounded-md font-semibold">Save</button>
            </div>
          </form>
        </div>
      )}
    </Layout>
  );
}

function FieldA({ label, children, required }) {
  return (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-[0.15em] text-slate-400 mb-1.5">
        {label}{required && <span className="text-red-400 ml-0.5">*</span>}
      </label>
      {React.cloneElement(children, {
        className: "w-full bg-background-deep border border-border rounded px-3 py-2 text-sm focus:outline-none focus:border-brand-cyan transition-colors",
      })}
    </div>
  );
}

function TwilioPreflight({ sid, token, fromNumber }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const disabled = !sid || !token || token === "********";

  const run = async () => {
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/alert-channels/twilio-preflight", {
        twilio_account_sid: sid,
        twilio_auth_token: token,
        twilio_from_number: fromNumber || null,
      });
      setResult(data);
    } catch (e) {
      setResult({ ok: false, message: formatApiError(e.response?.data?.detail) || e.message, details: {} });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-border rounded p-3 bg-background-deep/50" data-testid="twilio-preflight-panel">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-xs font-semibold text-white">Verify Twilio credentials</div>
          <div className="text-[11px] text-slate-500">Free check — pings Twilio&apos;s API without sending an SMS.</div>
        </div>
        <button type="button" onClick={run} disabled={disabled || busy}
          data-testid="twilio-preflight-btn"
          className="text-xs px-3 py-1.5 border border-brand-cyan/40 bg-brand-cyan/5 text-brand-cyan hover:bg-brand-cyan/15 rounded disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
          {busy ? "Checking…" : "Verify"}
        </button>
      </div>
      {result && (
        <div className={`mt-2 text-[11.5px] px-2.5 py-2 rounded border ${result.ok
          ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
          : "border-red-500/40 bg-red-500/10 text-red-300"}`}
          data-testid="twilio-preflight-result">
          <div className="font-medium">{result.ok ? "✓ " : "✗ "}{result.message}</div>
          {result.details && Object.keys(result.details).length > 0 && (
            <ul className="mt-1 space-y-0.5 opacity-90">
              {Object.entries(result.details).map(([k, v]) => (
                <li key={k}>· {k}: <span className="opacity-80">{String(v)}</span></li>
              ))}
            </ul>
          )}
        </div>
      )}
      {disabled && (
        <div className="mt-2 text-[11px] text-slate-500">
          Fill in Account SID and Auth Token to enable verify.
        </div>
      )}
    </div>
  );
}
